(() => {
  if (window.__ygoScrollSyncLoaded) return;
  window.__ygoScrollSyncLoaded = true;

  const MIN_WIDTH = 750;
  const ACTIVE_CLASS = "ygo-scroll-sync-enabled";
  const DESKTOP_POINTER = window.matchMedia("(hover: hover) and (pointer: fine)");
  const REDUCE_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

  const MODAL_SELECTOR = "product-modal.product-media-modal, .product-media-modal";
  const MEDIA_TRIGGER_SELECTOR = ".product__media-wrapper, media-gallery, .product__media-item, .product__media";
  const MEDIA_CLOSE_SELECTOR = ".product-media-modal__toggle";

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function shouldRunSync() {
    return window.innerWidth >= MIN_WIDTH || DESKTOP_POINTER.matches;
  }

  function cssNumber(element, name, fallback = 0) {
    const value = parseFloat(getComputedStyle(element).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  }

  /*
    offsetTop/offsetHeight ignorieren transform: translateY().
    Dadurch müssen wir die Sync-Klasse vor dem Messen nicht entfernen.
    Genau das reduziert Forced-Reflow-Risiko deutlich.
  */
  function docTop(element) {
    let top = 0;
    let node = element;

    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }

    return top;
  }

  function outerHeight(element) {
    const styles = getComputedStyle(element);

    return (
      element.offsetHeight +
      parseFloat(styles.marginTop || 0) +
      parseFloat(styles.marginBottom || 0)
    );
  }

  function getMediaModal() {
    return document.querySelector(MODAL_SELECTOR);
  }

  function isMediaModalOpen() {
    const modal = getMediaModal();
    if (!modal) return false;

    return (
      modal.hasAttribute("open") ||
      modal.classList.contains("active") ||
      modal.getAttribute("aria-hidden") === "false"
    );
  }

  function addMediaQueryListener(mediaQuery, callback) {
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", callback);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(callback);
    }
  }

  function preventFocusScrollTemporarily(duration = 900) {
    window.__ygoPreventFocusScrollUntil = Date.now() + duration;

    if (window.__ygoFocusPreventScrollPatched) return;
    window.__ygoFocusPreventScrollPatched = true;

    const nativeFocus = HTMLElement.prototype.focus;

    HTMLElement.prototype.focus = function ygoFocusWithoutScroll(options) {
      if (Date.now() < (window.__ygoPreventFocusScrollUntil || 0)) {
        try {
          const nextOptions =
            options && typeof options === "object"
              ? { ...options, preventScroll: true }
              : { preventScroll: true };

          return nativeFocus.call(this, nextOptions);
        } catch (error) {
          return nativeFocus.call(this);
        }
      }

      return nativeFocus.apply(this, arguments);
    };
  }

  function initProduct(productInfo) {
    if (!productInfo || productInfo.dataset.ygoScrollSyncReady === "true") return;

    const product = productInfo.querySelector(".product");
    const mediaWrapper = productInfo.querySelector(".product__media-wrapper");
    const infoWrapper = productInfo.querySelector(".product__info-wrapper");
    const infoContainer = productInfo.querySelector(".product__info-container");

    if (!product || !mediaWrapper || !infoWrapper || !infoContainer) return;

    productInfo.dataset.ygoScrollSyncReady = "true";
    productInfo.dataset.ygoModalRestoring = "false";
    productInfo.dataset.ygoViewportStabilizing = "false";

    let metrics = null;
    let currentMediaY = 0;
    let currentInfoY = 0;
    let targetMediaY = 0;
    let targetInfoY = 0;

    let measureRaf = null;
    let scrollRaf = null;
    let animationRaf = null;
    let pendingInstantMeasure = false;

    let modalWasOpen = false;
    let modalScrollY = null;
    let lastStableScrollY = window.scrollY;
    let restoreTimer = null;
    let viewportTimer = null;
    let viewportScrollY = null;
    let modalRestoreUntil = 0;

    function isRestoringModal() {
      return productInfo.dataset.ygoModalRestoring === "true";
    }

    function isStabilizingViewport() {
      return productInfo.dataset.ygoViewportStabilizing === "true";
    }

    function isTemporarilyBlocked() {
      return isRestoringModal() || isStabilizingViewport();
    }

    function setSyncVars(mediaY, infoY) {
      productInfo.style.setProperty("--ygo-sync-media-y", `${mediaY.toFixed(3)}px`);
      productInfo.style.setProperty("--ygo-sync-info-y", `${infoY.toFixed(3)}px`);
    }

    function cancelSyncFrames() {
      if (animationRaf) cancelAnimationFrame(animationRaf);
      if (measureRaf) cancelAnimationFrame(measureRaf);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);

      animationRaf = null;
      measureRaf = null;
      scrollRaf = null;
    }

    function disableSync() {
      cancelSyncFrames();

      metrics = null;
      currentMediaY = 0;
      currentInfoY = 0;
      targetMediaY = 0;
      targetInfoY = 0;

      productInfo.classList.remove(ACTIVE_CLASS);
      setSyncVars(0, 0);
    }

    function calculateTargets(instant = false) {
      scrollRaf = null;

      if (isTemporarilyBlocked()) return;

      if (isMediaModalOpen()) {
        handleModalStateChange();
        return;
      }

      if (!metrics || !shouldRunSync()) {
        disableSync();
        return;
      }

      lastStableScrollY = window.scrollY;

      const scrollY = window.scrollY;

      targetMediaY =
        metrics.maxMediaY > 0
          ? clamp(scrollY - metrics.mediaStartY, 0, metrics.maxMediaY)
          : 0;

      targetInfoY =
        metrics.maxInfoY > 0
          ? clamp(scrollY - metrics.infoStartY, 0, metrics.maxInfoY)
          : 0;

      if (instant) {
        currentMediaY = targetMediaY;
        currentInfoY = targetInfoY;
        setSyncVars(currentMediaY, currentInfoY);
        return;
      }

      startAnimation();
    }

    function animate() {
      animationRaf = null;

      if (!metrics || isTemporarilyBlocked() || isMediaModalOpen()) return;

      const ease = metrics.ease;

      if (ease >= 1) {
        currentMediaY = targetMediaY;
        currentInfoY = targetInfoY;
      } else {
        currentMediaY += (targetMediaY - currentMediaY) * ease;
        currentInfoY += (targetInfoY - currentInfoY) * ease;
      }

      if (Math.abs(targetMediaY - currentMediaY) < 0.08) {
        currentMediaY = targetMediaY;
      }

      if (Math.abs(targetInfoY - currentInfoY) < 0.08) {
        currentInfoY = targetInfoY;
      }

      setSyncVars(currentMediaY, currentInfoY);

      if (
        Math.abs(targetMediaY - currentMediaY) >= 0.08 ||
        Math.abs(targetInfoY - currentInfoY) >= 0.08
      ) {
        animationRaf = requestAnimationFrame(animate);
      }
    }

    function startAnimation() {
      if (animationRaf || isTemporarilyBlocked()) return;
      animationRaf = requestAnimationFrame(animate);
    }

    function measure() {
      measureRaf = null;

      const instant = pendingInstantMeasure;
      pendingInstantMeasure = false;

      if (isTemporarilyBlocked()) return;

      if (isMediaModalOpen()) {
        handleModalStateChange();
        return;
      }

      if (!shouldRunSync()) {
        disableSync();
        return;
      }

      /* Alle Layout-Reads gebündelt, bevor Layout-Writes kommen. */
      const offset = cssNumber(productInfo, "--ygo-sync-top-offset", 0);
      const mediaTop = docTop(mediaWrapper);
      const infoTop = docTop(infoWrapper);
      const mediaHeight = outerHeight(mediaWrapper);
      const infoHeight = outerHeight(infoWrapper);
      const mediaEndCorrection = cssNumber(productInfo, "--ygo-sync-media-end-correction", 0);
      const infoEndCorrection = cssNumber(productInfo, "--ygo-sync-info-end-correction", 0);

      const ease = REDUCE_MOTION.matches
        ? 1
        : clamp(cssNumber(productInfo, "--ygo-sync-ease", 0.28), 0.05, 1);

      const mediaBottom = mediaTop + mediaHeight;
      const infoBottom = infoTop + infoHeight;

      metrics = {
        mediaStartY: mediaTop - offset,
        infoStartY: infoTop - offset,
        maxMediaY:
          infoBottom > mediaBottom
            ? Math.max(0, infoBottom - mediaBottom + mediaEndCorrection)
            : 0,
        maxInfoY:
          mediaBottom > infoBottom
            ? Math.max(0, mediaBottom - infoBottom + infoEndCorrection)
            : 0,
        ease
      };

      productInfo.classList.add(ACTIVE_CLASS);
      calculateTargets(instant);
    }

    function requestMeasure(instant = false) {
      if (isTemporarilyBlocked()) return;

      if (isMediaModalOpen()) {
        handleModalStateChange();
        return;
      }

      pendingInstantMeasure = pendingInstantMeasure || instant;

      if (measureRaf) return;
      measureRaf = requestAnimationFrame(measure);
    }

    function requestScrollUpdate() {
      if (isTemporarilyBlocked()) return;

      if (isMediaModalOpen()) {
        handleModalStateChange();
        return;
      }

      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => calculateTargets(false));
    }

    function restoreScrollAfterModal() {
      if (isRestoringModal()) return;

      const y = Number.isFinite(modalScrollY) ? modalScrollY : lastStableScrollY;

      productInfo.dataset.ygoModalRestoring = "true";
      modalRestoreUntil = Date.now() + 900;

      preventFocusScrollTemporarily(900);
      cancelSyncFrames();

      if (viewportTimer) {
        clearTimeout(viewportTimer);
        viewportTimer = null;
      }

      viewportScrollY = null;

      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }

      window.scrollTo({
        top: y,
        left: window.scrollX,
        behavior: "auto"
      });

      if (restoreTimer) clearTimeout(restoreTimer);

      restoreTimer = setTimeout(() => {
        window.scrollTo({
          top: y,
          left: window.scrollX,
          behavior: "auto"
        });

        if (document.activeElement && mediaWrapper.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        currentMediaY = 0;
        currentInfoY = 0;
        targetMediaY = 0;
        targetInfoY = 0;

        modalScrollY = null;
        restoreTimer = null;

        productInfo.dataset.ygoModalRestoring = "false";
        requestMeasure(true);
      }, 220);
    }

    function handleModalStateChange() {
      const open = isMediaModalOpen();

      if (open && !modalWasOpen) {
        modalWasOpen = true;

        if (!Number.isFinite(modalScrollY)) {
          modalScrollY = window.scrollY;
        }

        cancelSyncFrames();
        return;
      }

      if (!open && modalWasOpen) {
        modalWasOpen = false;
        restoreScrollAfterModal();
      }
    }

    function stabilizeViewportAfterZoom() {
      if (
        isMediaModalOpen() ||
        isRestoringModal() ||
        Date.now() < modalRestoreUntil
      ) {
        return;
      }

      if (!isStabilizingViewport()) {
        productInfo.dataset.ygoViewportStabilizing = "true";
        viewportScrollY = window.scrollY;
        cancelSyncFrames();
      }

      if (viewportTimer) clearTimeout(viewportTimer);

      viewportTimer = setTimeout(() => {
        const y = Number.isFinite(viewportScrollY) ? viewportScrollY : window.scrollY;

        window.scrollTo({
          top: y,
          left: window.scrollX,
          behavior: "auto"
        });

        currentMediaY = 0;
        currentInfoY = 0;
        targetMediaY = 0;
        targetInfoY = 0;

        viewportScrollY = null;
        viewportTimer = null;

        productInfo.dataset.ygoViewportStabilizing = "false";
        requestMeasure(true);
      }, 220);
    }

    const resizeObserver = new ResizeObserver(() => requestMeasure(false));

    resizeObserver.observe(product);
    resizeObserver.observe(mediaWrapper);
    resizeObserver.observe(infoWrapper);
    resizeObserver.observe(infoContainer);

    mediaWrapper.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", () => requestMeasure(false), { once: true });
      }
    });

    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!productInfo.contains(event.target)) return;

        if (event.target.closest(MEDIA_TRIGGER_SELECTOR)) {
          modalScrollY = window.scrollY;
        }
      },
      true
    );

    document.addEventListener(
      "click",
      (event) => {
        if (event.target.closest(MEDIA_CLOSE_SELECTOR)) {
          setTimeout(handleModalStateChange, 0);
          setTimeout(handleModalStateChange, 80);
        }
      },
      true
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") {
          setTimeout(handleModalStateChange, 0);
          setTimeout(handleModalStateChange, 80);
        }
      },
      true
    );

    function observeModal() {
      const modal = getMediaModal();

      if (!modal || modal.dataset.ygoScrollSyncModalObserved === "true") return;

      modal.dataset.ygoScrollSyncModalObserved = "true";

      const modalObserver = new MutationObserver(handleModalStateChange);

      modalObserver.observe(modal, {
        attributes: true,
        attributeFilter: ["open", "class", "aria-hidden", "style"]
      });

      handleModalStateChange();
    }

    observeModal();

    setTimeout(observeModal, 500);
    setTimeout(observeModal, 1500);

    window.addEventListener("scroll", requestScrollUpdate, { passive: true });
    window.addEventListener("resize", stabilizeViewportAfterZoom, { passive: true });
    window.addEventListener("orientationchange", stabilizeViewportAfterZoom, { passive: true });
    window.addEventListener("load", () => requestMeasure(false), { once: true });

    addMediaQueryListener(DESKTOP_POINTER, stabilizeViewportAfterZoom);
    addMediaQueryListener(REDUCE_MOTION, () => requestMeasure(true));

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", stabilizeViewportAfterZoom, { passive: true });
      window.visualViewport.addEventListener("scroll", requestScrollUpdate, { passive: true });
    }

    requestMeasure(true);
  }

  function initAll(scope = document) {
    scope.querySelectorAll("product-info").forEach(initProduct);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();